/* eslint-disable no-console */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'

import mixpanel from '../utilities/mixpanel'
import { requestCreator } from '../utilities/requests'
import messagesChannelManager from '../channels/messages_channel'

const { get: validateToken, cancel: cancelValidationRequest } = requestCreator()
const { post: signIn } = requestCreator()
const { post: register, cancel: cancelRegistrationRequest } = requestCreator()

export const AuthContext = React.createContext()

export const authHeaderKeys = ['access-token', 'token-type', 'client', 'expiry', 'uid']

export function buildRegistrationInfo({ facilityId, fullName, preferredName, passwordConfirmation, ...rest }) {
  return {
    ...rest,
    ...(facilityId && { facility_id: facilityId }),
    ...(fullName && { full_name: fullName }),
    ...(preferredName && { preferred_name: preferredName }),
    ...(passwordConfirmation && {
      password_confirmation: passwordConfirmation
    })
  }
}

export function setAuthHeaders(config) {
  authHeaderKeys.forEach((key) => {
    const value = window.localStorage.getItem(key) || Cookies.get(key)
    if (value) {
      config.headers[key] = value
    }
  })

  return config
}

export function storeAuthHeaders(response) {
  authHeaderKeys.forEach((key) => {
    const value = response.headers[key] || window.localStorage.getItem(key) || Cookies.get(key)
    if (value) {
      window.localStorage.setItem(key, value)
      Cookies.set(key, value)
    } else {
      window.localStorage.removeItem(key)
      Cookies.remove(key)
    }
  })
  return response
}

export default function AuthProvider(props) {
  const isMountedRef = useRef(true)
  const [authState, setAuthState] = useState({
    isLoading: true,
    isSignedIn: false,
    currentUser: { facilitiesUsers: [] }
  })
  const value = useMemo(() => authState, [authState])

  function signOutUser() {
    messagesChannelManager.unsubscribe()
    authHeaderKeys.forEach((key) => {
      window.localStorage.removeItem(key)
      Cookies.remove(key)
    })
    setAuthState({
      isLoading: false,
      isSignedIn: false,
      currentUser: { facilitiesUsers: [] }
    })
  }

  const verifyCredentials = useCallback(() => {
    const storage = window.localStorage
    const token = storage.getItem('access-token') || Cookies.get('access-token')
    const client = storage.getItem('client') || Cookies.get('client')
    const uid = storage.getItem('uid') || Cookies.get('uid')
    if (token && client && uid) {
      setAuthState((previousState) => ({ ...previousState, isLoading: true }))
      return validateToken('/auth/validate_token', {
        headers: { 'access-token': token, client, uid }
      })
        .then((currentUser) => {
          setAuthState((previousState) => ({
            ...previousState,
            currentUser,
            isLoading: false,
            isSignedIn: true
          }))
          try {
            mixpanel.trackAuthEvent('Signed In', currentUser, {
              type: 'credentials verification'
            })
          } catch (mixpanelError) {
            console.warn('Mixpanel tracking failed:', mixpanelError)
          }
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            signOutUser()
            try {
              mixpanel.track('Credential Verification Failed')
            } catch (mixpanelError) {
              console.warn('Mixpanel tracking failed:', mixpanelError)
            }
            throw error
          }
        })
    } else {
      setAuthState((previousState) => ({
        ...previousState,
        isLoading: false
      }))
      signOutUser()
      return Promise.resolve()
    }
  }, [])

  function signInUser({ email, password }) {
    if (isMountedRef.current) {
      setAuthState((previousState) => ({ ...previousState, isLoading: true }))
    }
    return signIn('/auth/sign_in', {
      email,
      password
    })
      .then((currentUser) => {
        if (!currentUser) {
          console.error('Current user is null/undefined')
          throw new Error('Invalid response from server')
        }
        if (isMountedRef.current) {
          setAuthState((previousState) => ({
            ...previousState,
            currentUser,
            isLoading: false,
            isSignedIn: true
          }))
        }
        try {
          mixpanel.trackAuthEvent('Signed In', currentUser, {
            type: 'log in'
          })
        } catch (mixpanelError) {
          console.warn('Mixpanel tracking failed:', mixpanelError)
        }
      })
      .catch((error) => {
        console.error('Sign in error:', error)
        if (!axios.isCancel(error)) {
          if (isMountedRef.current) {
            setAuthState((previousState) => ({
              ...previousState,
              isLoading: false
            }))
          }
          try {
            mixpanel.track('Sign In Failed')
          } catch (mixpanelError) {
            console.warn('Mixpanel tracking failed:', mixpanelError)
          }
          throw error
        }
      })
  }

  function registerUser(registrationParams) {
    setAuthState((previousState) => ({ ...previousState, isLoading: true }))
    const registrationInfo = buildRegistrationInfo(registrationParams)

    return register('/auth', registrationInfo)
      .then((currentUser) => {
        setAuthState((previousState) => ({
          ...previousState,
          currentUser,
          isLoading: false,
          isSignedIn: true
        }))
        try {
          mixpanel.trackAuthEvent('Registered', currentUser)
        } catch (mixpanelError) {
          console.warn('Mixpanel tracking failed:', mixpanelError)
        }
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setAuthState((previousState) => ({
            ...previousState,
            isLoading: false
          }))
          signOutUser()
          try {
            mixpanel.track('Sign In Failed')
          } catch (mixpanelError) {
            console.warn('Mixpanel tracking failed:', mixpanelError)
          }
          throw error
        }
      })
  }

  useEffect(() => {
    if (!authState.isSignedIn) verifyCredentials()

    return () => {
      isMountedRef.current = false
      cancelValidationRequest()
      cancelRegistrationRequest()
    }
  }, [authState.isSignedIn, verifyCredentials])

  const authHeaders = useMemo(() => {
    const storage = window.localStorage
    const token = storage.getItem('access-token') || Cookies.get('access-token')
    const client = storage.getItem('client') || Cookies.get('client')
    const uid = storage.getItem('uid') || Cookies.get('uid')

    if (token && client && uid) {
      return {
        'access-token': token,
        client: client,
        uid: uid
      }
    }
    return null
  }, [authState.isSignedIn])

  return (
    <AuthContext.Provider
      value={{
        ...value,
        authHeaders,
        signInUser,
        registerUser,
        signOutUser,
        verifyCredentials
      }}
      {...props}
    />
  )
}

export { AuthProvider }
